/**
 * Format a duration given as seconds (e.g. funnel time-to-convert aggregates).
 */
export function formatDurationSeconds(totalSeconds) {
  if (totalSeconds == null || Number.isNaN(totalSeconds)) return "—";
  const s = Math.max(0, Number(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = Math.floor(s % 60);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(" ");
}

/**
 * Parse Postgres / human interval strings to total seconds (handles word forms
 * like "7 hours, 21 minutes, 31 seconds" that lack HH:MM:SS).
 */
export function parseIntervalLikeStringToSeconds(str) {
  if (str == null || str === "") return null;
  const s = String(str).trim();
  if (!s) return null;

  let days = 0;
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  let matched = false;

  const dm = s.match(/(\d+)\s*days?/i);
  if (dm) {
    days = parseInt(dm[1], 10);
    matched = true;
  }

  const hms = s.match(/(\d+):(\d+):(\d+)(?:\.(\d+))?/);
  if (hms) {
    matched = true;
    hours += parseInt(hms[1], 10);
    minutes += parseInt(hms[2], 10);
    seconds += parseInt(hms[3], 10);
  } else {
    const hm = s.match(/(\d+)\s*(?:hours?|hrs?)/i);
    const mm = s.match(/(\d+)\s*(?:minutes?|mins?)/i);
    const sm = s.match(/(\d+)\s*(?:seconds?|secs?)/i);
    if (hm || mm || sm) {
      matched = true;
      if (hm) hours += parseInt(hm[1], 10);
      if (mm) minutes += parseInt(mm[1], 10);
      if (sm) seconds += parseInt(sm[1], 10);
    } else {
      let cd = 0;
      let ch = 0;
      let cm = 0;
      let cs = 0;
      const compact = /\b(\d+)\s*([dhms])\b/gi;
      let m;
      while ((m = compact.exec(s)) !== null) {
        const n = parseInt(m[1], 10);
        const u = m[2].toLowerCase();
        if (u === "d") cd += n;
        if (u === "h") ch += n;
        if (u === "m") cm += n;
        if (u === "s") cs += n;
      }
      if (cd || ch || cm || cs) {
        matched = true;
        days += cd;
        hours += ch;
        minutes += cm;
        seconds += cs;
      }
    }
  }

  if (!matched) return null;
  const total = days * 86400 + hours * 3600 + minutes * 60 + seconds;
  return Number.isFinite(total) ? total : null;
}

/**
 * Format any interval value from API (seconds, pg object, or string) as compact d/h/m/s.
 */
export function formatDurationDisplay(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "number" && !Number.isNaN(value)) {
    return formatDurationSeconds(value);
  }
  if (typeof value === "object" && value !== null && !(value instanceof Date)) {
    const d = Number(value.days ?? value.day ?? 0) || 0;
    const h = Number(value.hours ?? value.hour ?? 0) || 0;
    const m = Number(value.minutes ?? value.minute ?? 0) || 0;
    const sec = Number(value.seconds ?? value.second ?? 0) || 0;
    const ms = Number(value.milliseconds ?? 0) || 0;
    const total = d * 86400 + h * 3600 + m * 60 + sec + ms / 1000;
    return formatDurationSeconds(total);
  }
  const parsed = parseIntervalLikeStringToSeconds(value);
  if (parsed != null && parsed >= 0) {
    return formatDurationSeconds(parsed);
  }
  return String(value);
}
